import { supabase } from '../lib/supabase';
import crypto from 'crypto-js';

/**
 * AI Response Cache Service
 * Hybrid approach: Use database for 80% predictable responses, AI for novel cases
 * Stores AI responses to reduce costs and improve speed
 */

export const aiCacheService = {
  /**
   * Generate cache key from prompt and context
   * @param {string} promptText - The prompt text
   * @param {Object} context - Context data (industry, difficulty, etc.)
   * @returns {string} - MD5 hash of prompt + context
   */
  generateCacheKey(promptText, context = {}) {
    const cacheString = JSON.stringify({
      prompt: promptText?.toLowerCase()?.trim(),
      industry: context.industry,
      difficulty: context.difficultyLevel,
      type: context.promptType
    });
    
    return crypto.MD5(cacheString).toString();
  },

  /**
   * Check if response exists in cache
   * @param {string} cacheKey - Cache key hash
   * @returns {Promise<Object|null>} - Cached response or null
   */
  async getCachedResponse(cacheKey) {
    try {
      const { data, error } = await supabase
        .from('ai_response_cache')
        .select('*')
        .eq('cache_key', cacheKey)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // No rows returned
        throw error;
      }

      // Update hit count and last_used_at
      await supabase
        .from('ai_response_cache')
        .update({
          hit_count: (data.hit_count || 0) + 1,
          last_used_at: new Date().toISOString()
        })
        .eq('id', data.id);

      return data.ai_response;
    } catch (error) {
      console.error('Error fetching cached response:', error);
      return null;
    }
  },

  /**
   * Store AI response in cache
   * @param {string} cacheKey - Cache key hash
   * @param {string} promptType - Type of prompt ('drill_evaluation', 'objection_evaluation', etc.)
   * @param {string} promptText - Original prompt
   * @param {Object} context - Context data
   * @param {Object} aiResponse - AI response to cache
   * @returns {Promise<void>}
   */
  async storeResponse(cacheKey, promptType, promptText, context, aiResponse) {
    try {
      const { error } = await supabase
        .from('ai_response_cache')
        .insert({
          cache_key: cacheKey,
          prompt_type: promptType,
          prompt_text: promptText,
          context_data: context,
          ai_response: aiResponse,
          hit_count: 0,
          created_at: new Date().toISOString(),
          last_used_at: new Date().toISOString()
        });

      if (error && error.code !== '23505') { // Ignore unique constraint violations
        throw error;
      }
    } catch (error) {
      console.error('Error storing cached response:', error);
    }
  },

  /**
   * Get or generate AI response (hybrid approach)
   * @param {string} promptText - Prompt text
   * @param {Object} context - Context data
   * @param {Function} aiGenerator - Function to call if cache miss
   * @returns {Promise<Object>} - AI response (cached or fresh)
   */
  async getOrGenerate(promptText, context, aiGenerator) {
    try {
      const cacheKey = this.generateCacheKey(promptText, context);
      
      // Try cache first
      const cachedResponse = await this.getCachedResponse(cacheKey);
      
      if (cachedResponse) {
        console.log('✅ Cache hit - using stored response');
        return {
          ...cachedResponse,
          fromCache: true
        };
      }

      // Cache miss - generate new response
      console.log('❌ Cache miss - calling AI');
      const freshResponse = await aiGenerator();

      // Store for future use (async, don't wait)
      this.storeResponse(
        cacheKey,
        context.promptType || 'unknown',
        promptText,
        context,
        freshResponse
      ).catch(err => console.warn('Failed to cache response:', err));

      return {
        ...freshResponse,
        fromCache: false
      };
    } catch (error) {
      console.error('Error in getOrGenerate:', error);
      // Fallback to direct AI call
      return await aiGenerator();
    }
  },

  /**
   * Get cache statistics
   * @returns {Promise<Object>} - Cache stats (total entries, total hits, etc.)
   */
  async getCacheStats() {
    try {
      const { data, error } = await supabase
        .from('ai_response_cache')
        .select('hit_count, prompt_type');

      if (error) throw error;

      const totalEntries = data?.length || 0;
      const totalHits = data?.reduce((sum, entry) => sum + (entry.hit_count || 0), 0) || 0;
      
      const byType = data?.reduce((acc, entry) => {
        const type = entry.prompt_type || 'unknown';
        if (!acc[type]) acc[type] = { count: 0, hits: 0 };
        acc[type].count++;
        acc[type].hits += entry.hit_count || 0;
        return acc;
      }, {});

      return {
        totalEntries,
        totalHits,
        avgHitsPerEntry: totalEntries > 0 ? (totalHits / totalEntries).toFixed(2) : 0,
        byType
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return { totalEntries: 0, totalHits: 0, avgHitsPerEntry: 0, byType: {} };
    }
  },

  /**
   * Clean old cache entries (keep only last 90 days)
   * @returns {Promise<number>} - Number of deleted entries
   */
  async cleanOldEntries() {
    try {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

      const { data, error } = await supabase
        .from('ai_response_cache')
        .delete()
        .lt('last_used_at', ninetyDaysAgo.toISOString())
        .select('id');

      if (error) throw error;
      
      const deletedCount = data?.length || 0;
      console.log(`Cleaned ${deletedCount} old cache entries`);
      
      return deletedCount;
    } catch (error) {
      console.error('Error cleaning old cache entries:', error);
      return 0;
    }
  }
};

export default aiCacheService;
